import { renderPullQuote } from '../pull-quote/pull-quote.component';
import { Video } from '../video/video-player/video-player.interface';
import { VideoContentAppearance } from '../../../../_storybook-helpers/video';
import { renderCta } from '../../css-based/call-to-action/call-to-action.component';

/**
 * This helper function renders the video content for the web component.
 * It is unusual because it renders both the slotted content (which gets replicated in FTLs) and is also directly
 * by the web component.
 *
 * This is done like this for the following reasons:
 *
 * - The component needs to re-render dynamically to be used with the playlist (when someone chooses a new playlist item)
 * - The content needs to be initially server-side rendered into the slot for SEO purposes
 * - We don't want to duplicate code, the rendering logic is exactly the same on the server as in the browser
 */

interface RenderVideoInnerContentOptions {
	appearance: VideoContentAppearance;
	video: Video;
	messages: Record<string, string>;
}

export const renderVideoInnerContent = ({
	appearance = 'full-width',
	video,
	messages,
}: RenderVideoInnerContentOptions) => {
	let headingFont;
	switch (appearance) {
		case 'full-width':
			headingFont = 'ds-heading-l';
			break;
		case 'one-column-small':
		case 'two-columns-small':
			headingFont = 'ds-heading-s-lc';
			break;
		case 'one-column-hero':
		case 'two-columns-hero':
			headingFont = 'ds-heading-m';
			break;
	}
	const {
		standard,
		callToAction,
		secondaryCallToAction,
		contentType,
		pullQuote,
		keyPoints,
	} = video;
	const description = standard?.description;

	return `
    <div class="ds-video-duration ds-eyebrow-l">
      <i class="fa fa-video" aria-hidden="true"></i>
      ${messages.video} ${video.duration}
    </div>
  
    ${
			(contentType === 'standard' || contentType === 'key-points') &&
			video.title
				? `
      <h2 class="ds-title ${headingFont}">${video.title}</h2>`
				: ''
		}
  
    ${
			contentType === 'standard' && description
				? `
      <div class="ds-description ds-body-m">
        ${description}
      </div>`
				: ''
		}
  
  ${
		contentType === 'key-points'
			? `
    <div class="ds-key-points"><ul>
      ${keyPoints
				.map(
					(kP) => `
      <li>
        <span
          class="ds-body-m-bold">
          ${kP.title ?? ''}</span><br>
		  <span class="ds-body-m ds-key-point-summary">
			  <p> 
				  ${kP.summary ?? ''}
			  </p>
		  </span>
      </li>`
				)
				.join('')}
  </ul></div>`
			: ''
	}
        
        ${
					contentType === 'pull-quote'
						? renderPullQuote({
								name: pullQuote.attributionName ?? '',
								jobTitle: pullQuote.attributionJobTitle ?? '',
								image: '',
								quote:
									(pullQuote?.pullQuoteText ?? '') || (pullQuote?.text ?? ''),
								alignment: 'left',
								isNested: true,
								authorNameLink: pullQuote.authorNameLink ?? '',
						  })
						: ''
				}
        
  ${
		(contentType === 'standard' || contentType === 'key-points') &&
		callToAction?.label
			? `
			<div class="ds-call-to-action">
				${renderCta({ cta: callToAction, secondaryCta: secondaryCallToAction })}
			</div>
			`
			: ''
	}`;
};
